import { Optional, ProfilePicture, ResumeSkillLevel } from 'packages/types'
import { RecommendationsRequiredAttributesType } from 'builder/components/FindJob/types'
import { DocumentTypes, FetchStatuses } from '../constants'
import { BriefResume } from '../panel'

export interface Answer {
  id: string
  text: string
  time?: number
}

export interface Question {
  id: string
  text: string
  hint?: string
  response?: Question
  time?: number
}

export type ChatPersona = 'explorer' | 'starter' | 'hurdler' | 'job_seeker'

export type PaywallModalType = 'pitch' | 'cover-letter' | 'interview-prep'

export type CareerInsightsPageType = 'insights' | 'pitch' | 'cover-letter'

export type TemporalModalType = 'insights-result' | 'promotion'

export interface QuestionFromApiResponse {
  question: string
  hint: string
  response: string
}

export interface QuestionListApiResponse {
  assessmentQuestions: QuestionFromApiResponse[]
}

export interface ResumeParserApiResponse {
  firstName: string
  middleName: string
  lastName: string
  summary: string
  pdf: string
  location: {
    formatted: string
    street: string
    city: string
    state: string
    country: string
    countryCode: string
  }
  emails: string[]
  profession: string
  positions: {
    title: string
    titleNormalized: string
    organization: string
    startDate: string
    endDate: string
    description: string
    location: {
      formatted: string
      street: string
      city: string
      state: string
      country: string
      countryCode: string
    }
    managementLevel: string
  }[]
  educations: {
    organization: string
    degree: string
    startDate: string
    endDate: string
    location: {
      formatted: string
      street: string
      city: string
      state: string
      country: string
      countryCode: string
    }
    educationLevel: string
  }[]
  socialUrls: string[]
  phoneNumbers: {
    countryCode: string
    countryName: string
    nationalNumber: string
  }[]
  languages: string[]
  detectedLanguage: string
  skills: {
    name: string
    numMonths: number
  }[]
  rawText: string
  error?: string
}

export interface ChatbotResultData {
  name: string
  userType: ChatPersona
  questionAnswers: Record<string, string>
  cvText: string
  currentJobPosition?: string
  targetPosition?: string
  includeCoverLetter?: boolean // true by default in backend
  cvStructured: StructuredResumeData
}

export enum CareerInsightsProcessingStatus {
  success = 'success',
  failure = 'failure',
  retry = 'retry',
  pending = 'pending',
}

export type CareerInsightsJobListData = {
  jobTitle: string[]
  salaryRange: string[]
  jobDescription: string[]
}

export type CareerInsightsSkillListData = {
  skill: string[]
  softHard: string[]
  coursesRecommendation: string[]
}

export type CareerAssessmentsResults = {
  careerPaths: string | undefined
  nextMove: string | undefined
  transferableSkills: string | undefined
  skillsCourses: string | undefined
  dataForGraphics: {
    progressionList: CareerInsightsJobListData
    transferrableSkillsList: CareerInsightsJobListData
    skillCoursesList: CareerInsightsSkillListData
  }
}

export type ProfessionalPitchResults = {
  diff: string
  close: string
  results: string
  positioning: string
}

export type CoverLetterResults = {
  close: string
  pitch: string
  results: string
  introduction: string
}

export type CareerInsightsData = {
  careerAssessmentId: number
  status: CareerInsightsProcessingStatus
  careerAssessmentResults: CareerAssessmentsResults | null
  professionalPitchResults: ProfessionalPitchResults | null
  coverLetterResults: CoverLetterResults | null
  currentJobPosition: string
}

export type CareerProfileCompletenessPendingKey =
  | 'target_role'
  | 'career_assessment'
  | 'work_experience'

export type CareerProfileCompletenessPendingItem = {
  key: CareerProfileCompletenessPendingKey
  value: number
}

export type CareerProfileCompletenessApiResponse = {
  completenessState: number
  completenessPending: CareerProfileCompletenessPendingItem[]
}

export type CareerProfileWorkExperienceData = {
  id: Optional<number | string>
  title?: Optional<string>
  employer?: Optional<string>
  city?: Optional<string>
  dateFrom?: Optional<string>
  dateUntil?: Optional<string>
  description?: Optional<string>
}

export type CareerProfileSkillData = {
  id: Optional<number | string>
  skill?: Optional<string>
  level?: Optional<ResumeSkillLevel>
}

export type CareerProfileEducationData = {
  id: Optional<number | string>
  school?: Optional<string>
  degree?: Optional<string>
  city?: Optional<string>
  dateFrom?: Optional<string>
  dateUntil?: Optional<string>
  description?: Optional<string>
}

export type CareerProfileCourseData = {
  id: Optional<number | string>
  course?: Optional<string>
  institution?: Optional<string>
  dateFrom?: Optional<string>
  dateUntil?: Optional<string>
}

export type TargetLocation = {
  id: number | null
  formattedName: string | null
}

export type TargetRole = {
  id: number | null
  standardTitle: string
}

export type PercentOfDaysWillingToTravel = '0' | '20' | '50' | '100'
export enum WorkingTypes {
  office = 'office',
  remote = 'remote',
  hybrid = 'hybrid',
}
export type SalaryPlan = 'monthly' | 'annually'
export type ExpectedStartWork = 'immediate' | 'one_month' | 'three_months' | 'six_months'

export type JobPreferenceAttributes = {
  expectedSalary?: number
  currentSalary?: number
  currentCurrency: string
  expectedSalaryPlan: SalaryPlan
  currentSalaryPlan: SalaryPlan
  expectedCurrency: string
  expectedWorkingTypes: Array<WorkingTypes>
  openForBusinessTrips?: boolean
  willingToRelocate: boolean
  willingToRelocateAtYourOwnCost: boolean
  expectedStartWork: ExpectedStartWork
  fullyVaccinatedAgreementAgainstCovid?: boolean
  hasRestrictionLimiting?: boolean
  openToRemoteOpportunities?: boolean
}

export type CareerProfileData = {
  id: Optional<number>
  email: string
  location: string
  hideSkillLevel: boolean
  targetRoles: TargetRole[]
  resumeTargetRoles: string[] | null
  targetLocations: TargetLocation[]
  profilePicture: ProfilePicture
  workExperiencesAttributes: CareerProfileWorkExperienceData[]
  skillsAttributes: CareerProfileSkillData[]
  educationsAttributes: CareerProfileEducationData[]
  coursesAttributes: CareerProfileCourseData[]
  newProfile: boolean
  autoApplyDataCompleted: boolean
  additionalPlansPage: Optional<string>

  firstName: string
  lastName: string
  currentJobTitle: string
  gender: string
  resumeUrl: string
  race: string
  fullAddress: string
  phoneNumber: string
  countriesAuthorizedToWorkIn: { value: string; label: string }[]
  socialLinksAttributes: Array<{
    title: string
    link: string
  }>

  jobPreferenceAttributes?: JobPreferenceAttributes
  resumeId: number | null
  resumeType: DocumentTypes | null
  resumeUploadedAt: string
  userType: string
  autoTailoring: boolean
  autoTailoringCl: boolean
  selectedCoverLetterId: number | null
} & Readonly<{
  userType: ChatPersona
}>

export type CareerProfileCardsType = Extract<
  keyof CareerProfileData,
  'workExperiencesAttributes' | 'skillsAttributes' | 'educationsAttributes' | 'coursesAttributes'
>

export class UpdateCareerProfileCardPayload<
  T extends CareerProfileCardsType = CareerProfileCardsType,
> {
  fieldName: T
  cardUpdate: {
    id: number
  } & Partial<CareerProfileData[T][number]>
}

export class MoveCareerProfileCardPayload<
  T extends CareerProfileCardsType = CareerProfileCardsType,
> {
  fieldName: T
  oldIndex: number
  newIndex: number
}

export class DeleteCareerProfileCardPayload<
  T extends CareerProfileCardsType = CareerProfileCardsType,
> {
  fieldName: T
  id: number
}

export type StructuredResumeData = {
  workExperiencesAttributes: Omit<CareerProfileWorkExperienceData, 'id'>[]
  skillsAttributes: Omit<CareerProfileSkillData, 'id'>[]
  educationsAttributes: Omit<CareerProfileEducationData, 'id'>[]
  coursesAttributes: Omit<CareerProfileCourseData, 'id'>[]
}

export interface CareerProfileStore {
  chat: {
    fetchQuestionsStatus: FetchStatuses
    fetchSelectedResumeStatus: FetchStatuses
    questions: Question[]
    asked: Question[]
    answers: Answer[]
    selectedResumeText: string | null
    selectedResume: BriefResume | null
    structuredResumeData: StructuredResumeData | null
    latestResume: BriefResume | null
    persona: ChatPersona | null
    isDataSentDuringCurrentSession: boolean
    uploadedFilename: string | null
  }
  careerInsights: {
    fetchCareerInsightsStatus: FetchStatuses
    data: CareerInsightsData | null
  }
  openedPaywallModalType: PaywallModalType | null
  isOptimizeResumeModalOpened: boolean
  completeness: {
    fetchStatus: FetchStatuses
    percent: number | null
    pendingItems: CareerProfileCompletenessPendingItem[]
    isResultClosed: boolean
  }
  seenCareerProfilePages: Record<CareerInsightsPageType, boolean>
  profile: {
    fetchStatus: FetchStatuses
    data: CareerProfileData | null
    isPhotoUploading: boolean
    updateProfileDataStatus: FetchStatuses
  }
  isSyncedWithLocalStorage: boolean
  careerInsightsStatusModalType: CareerInsightsPageType | null
  seenTemporalModals: Record<TemporalModalType, boolean>
  floatingHelperPromotionDays: string[] // array of string dates
  autoApply: {
    isComplete: boolean
    fetchStatus: FetchStatuses
  }
  mobileProfileMenu: boolean
  onboarding: {
    isNewProfile: boolean
    fetchIsNewProfileStatus: FetchStatuses
    structuredResumeData: StructuredResumeData | null
    wasProfilePrefilled: boolean
  }
  recommendations: {
    missingAttributes: RecommendationsRequiredAttributesType[]
  }
}

export type LocalStorageCareerProfileState = Pick<
  CareerProfileStore,
  'chat' | 'seenCareerProfilePages' | 'seenTemporalModals' | 'floatingHelperPromotionDays'
> & {
  completeness: Pick<CareerProfileStore['completeness'], 'isResultClosed'>
}
